import {
  computeFillDownBox,
  computeIsFillDownActive,
  computeIsFillDownEnabled,
  computeIsFillDownInvalid,
} from 'notebook/tables/store/logic/TableFillDownLogic';
import {createTableModelSelector} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore} from 'notebook/tables/store/TableStore';
import {MultiSelectionBox} from 'notebook/tables/TableConstants';

/**
 * Model containing fill-down state for a table.
 * Fill-down allows users to drag a handle to repeat values from a selection
 * into adjacent cells below.
 *
 * TODO(BNCH-146929): Future properties to add:
 * - isLocked: boolean (from _isFillDownBoxEditingLocked)
 * - renderBox: MultiSelectionBox | null (from getFillDownRenderBox)
 * - doubleClickBox: MultiSelectionBox | null (from getDoubleClickFillDownBox)
 */
export interface TableFillDownModel {
  /**
   * Whether fill-down is enabled for this table as a feature.
   * True when enableFillDown prop is set or formulas are not disabled.
   */
  isEnabled: boolean;

  /**
   * Whether a fill-down operation is currently active (user is dragging the fill handle).
   */
  isActive: boolean;

  /**
   * Whether the current fill-down operation is invalid (e.g. dragging over merged regions).
   */
  isInvalid: boolean;

  /**
   * The fill-down box representing the range of cells being filled.
   * Null/undefined when no fill-down operation is in progress.
   */
  box: MultiSelectionBox | null | undefined;
}

/**
 * Creates a memoized selector that computes the TableFillDownModel from the store.
 */
export function createTableFillDownSelector(): (store: TableEditableStore) => TableFillDownModel {
  return createTableModelSelector(
    [
      (store) => store.stableProps.enableFillDown,
      (store) => store.stableProps.disableFormulas ?? false,
      (store) => store.state.multiExtra.fillDownActive,
      (store) => store.state.multiExtra.fillDownInvalid,
      (store) => store.state.multiExtra.fillDownBox,
    ],
    (enableFillDown, disableFormulas, fillDownActive, fillDownInvalid, fillDownBox) => ({
      isEnabled: computeIsFillDownEnabled(enableFillDown, disableFormulas),
      isActive: computeIsFillDownActive(fillDownActive),
      isInvalid: computeIsFillDownInvalid(fillDownInvalid),
      box: computeFillDownBox(fillDownBox),
    })
  );
}
